import { css } from '@emotion/react';
import React, { useEffect } from 'react';
import { useAppDispatch } from 'redux/store';
import { hexToRgbaString, highTide, primary, success } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import { closeEditTranslationComponent, showEditTranslationComponent } from 'redux/actions/edit-translations';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { EditableCourseElements, EditableElements } from 'redux/schemas/models/edit-translations';
import { CourseOutlineItem } from 'redux/selectors/course-outline/course-outline-item';

import { config } from '../../../../config/pendo.config.json';

import NvTooltip from '../nv-tooltip';
import NvIcon from '../nv-icon';

type EditEditTranslationToolsProps = {
  hasBeenEdited: boolean,
  type: EditableElements,
  component: CourseOutlineItem | LectureComponent,
};

const EditTranslationTools = ({
  hasBeenEdited = false,
  type,
  component,
}: EditEditTranslationToolsProps) => {
  const dispatch = useAppDispatch();
  const editTranslationStyles = css`
    .icon-translate {
      color: ${primary};
      &:hover {
        color: ${highTide};
      }
    }
    .outer-circle {
      margin-top: ${quarterSpacing}px;
      width: 12px;
      height: 12px;
      background-color: ${hexToRgbaString(success, 0.1)};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .inner-circle {
      width: 6px;
      height: 6px;
      background-color: ${success};
      border-radius: 50%;
    }
  `;

  const showEditableTranslationComponent = () => {
    let dataComponent;
    let dataSubType;
    const editableCourseElements = Object.values(EditableCourseElements) as string[];
    if (editableCourseElements.includes(type)) {
      dataComponent = { ...component } as CourseOutlineItem;
    } else {
      dataComponent = { ...component } as LectureComponent;
      // For styled links, we will edit the header/title, not the content/URL
      if (type === ComponentType.STYLED_LINK) {
        dataComponent.content = dataComponent.header;
      }
      // For Rich Text and HTML components they have different subtypes or trueTypes
      if (type === ComponentType.RICH_TEXT) {
        dataSubType = dataComponent.trueType;
      }
    }
    dispatch(showEditTranslationComponent({
      type,
      component: dataComponent,
      trueType: dataSubType,
    }));
  };

  useEffect(() => (() => dispatch(closeEditTranslationComponent())), []);

  return (
    <div css={editTranslationStyles}>
      <NvTooltip
        placement='top'
        preventOverflow={false}
        text={t.AUTOMATED_TRANSLATION.OPTION_BAR.EDIT_TRANSLATION_TOOLTIP()}
      >
        <ClickableContainer
          onClick={showEditableTranslationComponent}
          aria-label={t.AUTOMATED_TRANSLATION.OPTION_BAR.EDIT_TRANSLATION_TOOLTIP()}
          data-qa={config.pendo.novoAiTranslation.editTranslation}
        >
          <NvIcon icon='translate' size='xss-smallest' />
        </ClickableContainer>
      </NvTooltip>
      {
        hasBeenEdited && (
          <NvTooltip
            placement='top'
            preventOverflow={false}
            text={t.AUTOMATED_TRANSLATION.OPTION_BAR.TRANSLATION_EDITED_TOOLTIP()}
          >
            <div className='outer-circle'>
              <div className='inner-circle' />
            </div>
          </NvTooltip>
        )
      }
    </div>
  );
};

export default EditTranslationTools;
